import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Icon } from "@/components/Icon";

import { FaqList } from "@/components/FaqAccordion";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { AGENCY } from "@/lib/agency";
import { buildHead, orgRef } from "@/lib/seo";
import { absoluteUrl } from "@/lib/site-config";
import { ContentByline } from "@/components/ContentByline";
import {
  getDallasPillar,
  dallasMoneyPillars,
  dallasIndustryPillars,
  type DallasPage,
} from "@/lib/dallas-content";
import { getLoanProduct, type LoanProduct } from "@/lib/loan-products";
import { getPillarContent, type PillarContent } from "@/lib/pillar-content";

const PRODUCT_ICON_NAME: Record<string, string> = {
  "sba-loans": "award",
  "sba-7a-loan": "badgecheck",
  "sba-loan-for-daycare": "heart",
  "sba-loan-for-franchise": "store",
  "sba-express-loan-for-veterans": "shieldcheck",
  "working-capital-loans": "wallet",
  "business-line-of-credit": "creditcard",
  "small-business-loans": "briefcase",
  "short-term-business-loans": "zap",
  "merchant-cash-advance": "receipt",
  "unsecured-business-loans": "sparkles",
  "startup-business-loans": "lightbulb",
  "business-acquisition-loans": "handcoins",
  "accounts-receivable-financing": "filetext",
  "invoice-factoring": "receipt",
  "revenue-based-financing": "trendingup",
  "same-day-business-loans": "clock",
  "business-lenders-near-me": "mappin",
  "business-loan-broker": "compass",
  "commercial-loan-broker": "building2",
  "equipment-financing": "wrench",
  "manufacturing-equipment-financing": "factory",
  "agriculture-equipment-financing": "tractor",
  "landscaping-equipment-financing": "scissors",
  "dental-practice-loans": "stethoscope",
  "medical-practice-loans": "stethoscope",
  "restaurant-loans": "utensils",
  "trucking-business-loans": "truck",
  "commercial-construction-loan": "hammer",
  "hotel-loans": "hotel",
  "salon-business-loans": "scissors",
  "retail-business-loans": "store",
  "auto-repair-shop-financing": "car",
  "ecommerce-business-funding": "shoppingbag",
  "food-truck-financing": "coffee",
  "loan-for-gym-business": "dumbbell",
  "farm-credit-financing": "sprout",
  "women-owned-business-funding": "users",
};
const iconFor = (slug: string) => PRODUCT_ICON_NAME[slug] ?? "dollarsign";

/* ---------- Imagery (Unsplash, descriptive alt at use site) ---------- */
const u = (id: string, w = 1100) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=70`;

const HERO_POOL = [
  u("photo-1556761175-5973dc0f32e7"), // advisor + owner
  u("photo-1521791136064-7986c2920216"), // handshake
  u("photo-1454165804606-c3d57bc86b40"), // laptop business
  u("photo-1573497019940-1c28c88b4f3e"), // small business owner
  u("photo-1556742502-ec7c0e9f34b1"), // money / capital
  u("photo-1554224155-1696413565d3"), // line of credit
  u("photo-1581094794329-c8112a89af12"), // equipment
  u("photo-1450101499163-c8848c66ca85"), // invoices / paperwork
  u("photo-1556761175-b413da4baf72"), // owner storefront
  u("photo-1504307651254-35680f356dfd"), // construction
  u("photo-1517248135467-4c7edcad34c4"), // restaurant
  u("photo-1576091160550-2173dba999ef"), // medical
];
function pickImage(slug: string) {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return HERO_POOL[h % HERO_POOL.length];
}

export const Route = createFileRoute("/dallas/$slug/")({
  loader: ({ params }) => {
    const pillar = getDallasPillar(params.slug);
    if (!pillar) throw notFound();
    return { pillar };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const p = loaderData.pillar;
    const path = `/dallas/${params.slug}/`;
    const desc = buildMetaDescription(p);
    const img = pickImage(params.slug);
    const { faqs, serviceNode } = buildPillarSchema(p, path);
    const head = buildHead({
      title: `${p.h1} | ${AGENCY.name}`,
      description: desc,
      path,
      image: img,
      type: "article",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Business Loans", path: "/dallas/" },
        { name: p.pillar, path },
      ],
      faqs,
      graph: [serviceNode],
    });
    // Preload the eager above-the-fold hero so the browser fetches the LCP
    // image immediately instead of after HTML parse/hydration.
    return {
      ...head,
      links: [
        ...head.links,
        { rel: "preload", as: "image", href: img, fetchPriority: "high" },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-4xl font-semibold">Program not found</h1>
      <p className="mt-3 text-muted-foreground">
        That Dallas loan program doesn't exist. Browse all programs from the
        Dallas hub.
      </p>
      <Button asChild className="mt-6 rounded-full">
        <Link to="/dallas">Back to Dallas hub</Link>
      </Button>
    </div>
  ),
  component: PillarPage,
});

/* ---------- SEO helpers ---------- */
function buildMetaDescription(p: DallasPage) {
  return `${p.pillar} in Dallas, TX. Typical amounts, terms, and funding timelines explained. Arranged by ${AGENCY.name} - no upfront fees, decisions in hours.`.slice(
    0,
    158,
  );
}

function cap(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function faqAnswer(q: string, p: DallasPage, product?: LoanProduct): string {
  const range = product?.range ?? "$10K to $5M";
  const speed = product?.speed ?? "as little as 24-72 hours";
  return `For Dallas, TX businesses exploring ${p.pillar.toLowerCase()}, typical amounts run ${range} with funding in ${speed}. Final rates and terms depend on the lender and borrower profile. ${AGENCY.name} compares multiple lenders on your behalf at no upfront cost - call ${AGENCY.phone} to speak with a Texas-based advisor.`;
}

function buildPillarSchema(
  p: DallasPage,
  path: string,
): { faqs: { q: string; a: string }[]; serviceNode: Record<string, unknown> } {
  const serviceNode: Record<string, unknown> = {
    "@type": "Service",
    name: p.h1,
    serviceType: p.pillar,
    provider: orgRef,
    areaServed: { "@type": "City", name: "Dallas" },
    url: absoluteUrl(path),
  };

  const content = p.slug ? getPillarContent(p.slug) : undefined;
  const faqEntries =
    content?.faq && content.faq.length
      ? content.faq.map((f) => ({ q: f.q, a: f.a }))
      : p.faqQuestions.map((q) => ({
          q,
          a: faqAnswer(q, p, p.slug ? getLoanProduct(p.slug) : undefined),
        }));

  // Normalize each question to end with "?" so visible text and schema match.
  const faqs = faqEntries.map((f) => ({
    q: /\?$/.test(f.q) ? cap(f.q) : `${cap(f.q)}?`,
    a: f.a,
  }));

  return { faqs, serviceNode };
}

/* ---------- Page shell ---------- */
function PillarPage() {
  const { pillar } = Route.useLoaderData();
  const product = pillar.slug ? getLoanProduct(pillar.slug) : undefined;
  const content = pillar.slug ? getPillarContent(pillar.slug) : undefined;
  const isIndustry = pillar.pageType === "Vertical Pillar";

  const related = (isIndustry ? dallasIndustryPillars : dallasMoneyPillars)
    .filter((x) => x.slug !== pillar.slug)
    .slice(0, 6);

  const heroImg = pickImage(pillar.slug || pillar.pillar);

  return (
    <>
      <article>
        <Hero pillar={pillar} product={product} content={content} heroImg={heroImg} />
        <div className="border-b border-border bg-background">
          <div className="mx-auto max-w-3xl px-6 py-4 lg:px-10">
            <ContentByline />
          </div>
        </div>
        <WhatItIs pillar={pillar} product={product} content={content} />
        <BestFor pillar={pillar} product={product} content={content} />
        <TypicalTerms pillar={pillar} product={product} content={content} />
        <HowItWorks pillar={pillar} />
        <Benefits pillar={pillar} />
        <CommonUses pillar={pillar} product={product} content={content} />
        <Faq pillar={pillar} product={product} content={content} />
        <Related related={related} isIndustry={isIndustry} />
        <FinalCta pillar={pillar} />
      </article>
      <MobileStickyBar applyHref="/apply-funding" />
    </>
  );
}

/* ---------- Hero ---------- */
function Hero({
  pillar,
  product,
  content,
  heroImg,
}: {
  pillar: DallasPage;
  product?: LoanProduct;
  content?: PillarContent;
  heroImg: string;
}) {
  const benefits = [
    "No upfront fees, ever",
    "Compare multiple lenders in one call",
    "Texas-based advisors",
    "Soft credit pull (no credit hit to quote)",
  ];
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 85% 0%, oklch(0.42 0.16 250) 0%, transparent 55%), radial-gradient(ellipse at 0% 100%, oklch(0.3 0.1 260) 0%, transparent 50%)",
        }}
      />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-14 md:py-20 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10 lg:px-10">
        <Reveal>
          <nav
            aria-label="Breadcrumb"
            className="flex flex-wrap items-center gap-1.5 text-xs text-primary-foreground/70"
          >
            <Link to="/" className="hover:text-accent">
              Home
            </Link>
            <Icon name="chevronright" color="bl" className="h-3 w-3 brightness-0 invert"  />
            <Link to="/dallas" className="hover:text-accent">
              Business Loans
            </Link>
            <Icon name="chevronright" color="bl" className="h-3 w-3 brightness-0 invert"  />
            <span className="text-primary-foreground/85">{pillar.pillar}</span>
          </nav>

          <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-foreground/85">
            <Icon name="mappin" color="bl" className="h-3 w-3 text-accent brightness-0 invert"  /> Serving Dallas-Fort Worth
          </p>
          <h1 className="mt-4 font-serif text-4xl font-semibold leading-[1.05] tracking-tight md:text-5xl lg:text-6xl">
            {pillar.pillar} in Dallas, TX
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-primary-foreground/85">
            {content?.whatItIs?.[0] ??
              product?.description ??
              `${pillar.pillar} is a financing program designed for Dallas-area small and mid-sized businesses that need capital faster than a traditional bank can deliver.`}
          </p>

          <ul className="mt-6 grid max-w-xl gap-2 text-sm text-primary-foreground/90 sm:grid-cols-2">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <Icon name="checkcircle2" color="bl" className="mt-0.5 h-4 w-4 shrink-0 text-accent brightness-0 invert"  />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-7 flex flex-wrap gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
          </div>
        </Reveal>

        {/* Key-facts card + image */}
        <Reveal delay={120} className="relative">
          <div className="relative overflow-hidden rounded-3xl border border-primary-foreground/15 bg-primary-foreground/5 shadow-2xl backdrop-blur">
            <div className="relative h-44 w-full overflow-hidden sm:h-56">
              <img
                src={heroImg}
                alt={`${pillar.pillar} for Dallas-area business owners`}
                loading="eager"
                decoding="async"
                className="h-full w-full object-cover"
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent"
              />
            </div>
            <div className="p-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
                Program at a glance
              </p>
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4">
                <Fact
                  icon="wallet"
                  label="Typical loan amount"
                  value={content?.terms?.amount ?? product?.range ?? "$10K - $5M"}
                />
                <Fact
                  icon="clock"
                  label="Funding speed"
                  value={content?.terms?.speed ?? product?.speed ?? "24h - 30d"}
                />
                <Fact
                  icon="clock"
                  label="Term length"
                  value={content?.terms?.term ?? termLengthFor(pillar, product)}
                />
                <Fact
                  icon="filetext"
                  label="To qualify"
                  value={content?.terms?.qualifications ?? "6+ mo. operating · bank statements"}
                />
              </dl>
              <p className="mt-5 text-[11px] leading-relaxed text-primary-foreground/65">
                Ranges are typical for Dallas-area files. Final rates and terms
                depend on the lender and borrower profile.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Fact({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-foreground/65">
        <Icon name={icon} color="co" className="h-3.5 w-3.5" /> {label}
      </dt>
      <dd className="mt-1 font-serif text-lg leading-snug text-primary-foreground">
        {value}
      </dd>
    </div>
  );
}

function termLengthFor(p: DallasPage, product?: LoanProduct): string {
  const slug = (product?.slug || p.slug || "").toLowerCase();
  if (slug.includes("line-of-credit")) return "Revolving · 6-24 mo.";
  if (slug.includes("sba")) return "5-25 years";
  if (slug.includes("equipment")) return "2-7 years";
  if (slug.includes("short-term")) return "3-18 months";
  if (slug.includes("merchant") || slug.includes("same-day")) return "3-18 months";
  if (slug.includes("factoring") || slug.includes("receivable")) return "Per invoice · 30-90 days";
  if (slug.includes("acquisition") || slug.includes("construction") || slug.includes("hotel"))
    return "5-25 years";
  return "6 months - 10 years";
}

/* ---------- What it is ---------- */
function WhatItIs({
  pillar,
  product,
  content,
}: {
  pillar: DallasPage;
  product?: LoanProduct;
  content?: PillarContent;
}) {
  const pname = pillar.pillar;
  const paragraphs =
    content?.whatItIs && content.whatItIs.length
      ? content.whatItIs
      : [
          product?.description ??
            `${pname} is a financing program structured for established small and mid-sized businesses that need capital quickly and on terms that match their cash cycle.`,
          `In practice, you apply once with a short application and a few months of business bank statements. We compare offers from lenders actively writing ${pname.toLowerCase()} for Texas borrowers, walk you through the real cost of capital (not just the headline rate), and you choose the offer that fits.`,
        ];
  return (
    <section className="border-b border-border bg-background">
      <div className="mx-auto max-w-3xl px-6 py-14 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            What it is
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            {pname}, explained in plain English
          </h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            <p>
              {AGENCY.name} is not a lender. We're a Dallas-based advisory
              firm that places your file with the lender most likely to fund
              it on the best terms, and we get paid only when your deal
              closes.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Who it's best for ---------- */
function BestFor({
  pillar,
  product,
  content,
}: {
  pillar: DallasPage;
  product?: LoanProduct;
  content?: PillarContent;
}) {
  const items =
    content?.bestForBusinesses && content.bestForBusinesses.length
      ? content.bestForBusinesses
      : product?.bestFor && product.bestFor.length
        ? product.bestFor
        : defaultBestFor(pillar);
  const situations =
    content?.situations && content.situations.length
      ? content.situations
      : situationsFor(pillar, product);

  return (
    <section className="border-b border-border bg-secondary/40">
      <div className="mx-auto max-w-6xl px-6 py-14 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Who it's best for
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            A good fit if you recognize yourself here
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            {pillar.pillar} tends to be the right choice in specific
            situations. If one or more of these describes you, it's worth a
            15-minute call.
          </p>
        </Reveal>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          <Reveal>
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-foreground">
              Business types
            </h3>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {items.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 rounded-xl border border-border bg-background p-4"
                >
                  <Icon name="building2" color="co" className="mt-0.5 h-5 w-5 shrink-0"  />
                  <span className="text-sm">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={100}>
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-foreground">
              Situations it fits
            </h3>
            <ul className="mt-4 grid gap-3">
              {situations.map((s) => (
                <li
                  key={s}
                  className="flex items-start gap-3 rounded-xl border border-border bg-background p-4"
                >
                  <Icon name="checkcircle2" color="co" className="mt-0.5 h-5 w-5 shrink-0"  />
                  <span className="text-sm">{s}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function defaultBestFor(p: DallasPage): string[] {
  return [
    `Established ${p.pillar.toLowerCase()} candidates`,
    "Owners declined or stalled by a national bank",
    "Businesses operating 6+ months with steady deposits",
    "Owners who want to compare offers, not commit early",
  ];
}

function situationsFor(p: DallasPage, product?: LoanProduct): string[] {
  const slug = (product?.slug || p.slug || "").toLowerCase();
  const base = [
    "You need capital faster than your bank can move",
    "You've been declined or quoted poor terms elsewhere",
    "You want a transparent breakdown of total cost, not just APR",
  ];
  if (slug.includes("line-of-credit"))
    base.push("Cash flow swings month to month and you want a reusable cushion");
  if (slug.includes("equipment"))
    base.push("You're buying a specific asset and want it as collateral");
  if (slug.includes("sba"))
    base.push("You qualify for SBA terms and can wait 30-60 days to fund");
  if (slug.includes("merchant") || slug.includes("same-day"))
    base.push("You have an urgent expense and steady card or deposit volume");
  if (slug.includes("factoring") || slug.includes("receivable"))
    base.push("You invoice B2B customers on 30-90 day terms");
  if (slug.includes("startup"))
    base.push("You're under two years old and traditional lenders won't quote");
  if (slug.includes("acquisition"))
    base.push("You're buying an existing business or buying out a partner");
  return base.slice(0, 5);
}

/* ---------- Typical terms ---------- */
function TypicalTerms({
  pillar,
  product,
  content,
}: {
  pillar: DallasPage;
  product?: LoanProduct;
  content?: PillarContent;
}) {
  const t = content?.terms;
  const rows = [
    { label: "Typical loan amount", value: t?.amount ?? product?.range ?? "$10K - $5M" },
    { label: "Term length", value: t?.term ?? termLengthFor(pillar, product) },
    { label: "Time to funding", value: t?.speed ?? product?.speed ?? "24 hours - 30 days" },
    {
      label: "Qualification basics",
      value:
        t?.qualifications ??
        "Business 6+ months operating, 3 months of business bank statements, U.S. business entity",
    },
    {
      label: "Documentation",
      value:
        t?.documentation ??
        "One-page application; tax returns generally not required under $250K",
    },
    { label: "Cost to apply", value: "$0 upfront. No fees, no obligation." },
  ];
  return (
    <section className="border-b border-border bg-background">
      <div className="mx-auto max-w-5xl px-6 py-14 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Typical terms
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            The numbers, as straight as we can give them
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            These are typical ranges for {pillar.pillar.toLowerCase()} files
            we place for Dallas-area borrowers. Final rates and terms depend
            on the lender and the borrower profile.
          </p>
        </Reveal>
        <Reveal delay={80}>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-background">
            {rows.map((r) => (
              <div
                key={r.label}
                className="grid gap-1 px-5 py-4 sm:grid-cols-[220px_1fr] sm:items-center sm:gap-6"
              >
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {r.label}
                </dt>
                <dd className="font-serif text-base text-foreground">
                  {r.value}
                </dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- How to apply ---------- */
function HowItWorks({ pillar }: { pillar: DallasPage }) {
  const steps = [
    {
      n: "01",
      h: "Talk to a Dallas advisor",
      p: `Quick 15-minute call. We confirm ${pillar.pillar.toLowerCase()} is the right structure, or recommend a better-fitting program.`,
    },
    {
      n: "02",
      h: "Submit your file",
      p: "One-page application plus 3 months of business bank statements. Most files don't require tax returns.",
    },
    {
      n: "03",
      h: "Compare real offers",
      p: "Within 24-72 hours we bring back 2-4 funded offers from lenders actively writing this program for Texas borrowers.",
    },
    {
      n: "04",
      h: "Sign and fund",
      p: "Pick the offer that fits. Funds land in your business account, often the same business day.",
    },
  ];
  return (
    <section
      id="apply"
      className="relative overflow-hidden border-b border-border bg-secondary/40 scroll-mt-20"
    >
      <div className="mx-auto max-w-6xl px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            How to apply
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            From first call to funded in days
          </h2>
        </Reveal>
        <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <Reveal key={s.n} as="li" delay={i * 80}>
              <div className="relative h-full rounded-2xl border border-border bg-background p-6">
                <span className="font-serif text-4xl font-semibold text-accent">
                  {s.n}
                </span>
                <h3 className="mt-3 text-base font-semibold">{s.h}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.p}</p>
              </div>
            </Reveal>
          ))}
        </ol>
        <Reveal delay={320}>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full">
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full"
            >
              <a href={AGENCY.phoneHref}>Start my application</a>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Benefits ---------- */
function Benefits({ pillar }: { pillar: DallasPage }) {
  const benefits = [
    {
      icon: "zap",
      h: "Decisions in hours",
      p: `Files for ${pillar.pillar.toLowerCase()} are usually screened the same day they arrive.`,
    },
    {
      icon: "handcoins",
      h: "No upfront fees",
      p: "We're paid by the lender on close. You see real offers before any commitment.",
    },
    {
      icon: "layers",
      h: "Multiple lenders, one file",
      p: "One application, side-by-side offers, so you choose on facts, not pressure.",
    },
    {
      icon: "shieldcheck",
      h: "Texas-based advisors",
      p: "Local underwriters who understand Dallas business profiles and Texas filings.",
    },
    {
      icon: "filecheck",
      h: "Soft credit pull to quote",
      p: "Initial review is a soft pull, with no impact to your personal credit score.",
    },
    {
      icon: "trendingup",
      h: "Built around your cash cycle",
      p: "Term, draw structure, and payment cadence sized to how your revenue actually arrives.",
    },
  ];
  return (
    <section className="border-b border-border bg-background">
      <div className="mx-auto max-w-6xl px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Benefits
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            Why Dallas owners pick {pillar.pillar.toLowerCase()} through us
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((b, i) => (
            <Reveal key={b.h} delay={i * 60}>
              <div className="group h-full rounded-2xl border border-border bg-background p-6 transition hover:-translate-y-0.5 hover:border-accent hover:shadow-lg">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
                  <Icon name={b.icon} color="co" className="h-5 w-5"  />
                </div>
                <h3 className="mt-4 font-serif text-lg font-semibold">
                  {b.h}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {b.p}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Common uses ---------- */
function CommonUses({
  pillar,
  product,
  content,
}: {
  pillar: DallasPage;
  product?: LoanProduct;
  content?: PillarContent;
}) {
  const uses =
    content?.commonUses && content.commonUses.length
      ? content.commonUses
      : commonUsesFor(pillar, product);
  return (
    <section className="border-b border-border bg-secondary/40">
      <div className="mx-auto max-w-6xl px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Common uses
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            How Dallas businesses actually use it
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            A few of the real-world reasons {pillar.pillar.toLowerCase()}{" "}
            shows up in our pipeline each month.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {uses.map((u, i) => (
            <Reveal key={u.h} delay={i * 60}>
              <div className="h-full rounded-2xl border border-border bg-background p-6">
                <div className="flex items-center gap-2 text-accent">
                  <Icon name="sparkles" color="co" className="h-5 w-5"  />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">
                    Use case
                  </span>
                </div>
                <h3 className="mt-3 font-serif text-lg font-semibold">
                  {u.h}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {u.p}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function commonUsesFor(
  p: DallasPage,
  product?: LoanProduct,
): { h: string; p: string }[] {
  const slug = (product?.slug || p.slug || "").toLowerCase();
  const generic = [
    {
      h: "Cover a payroll gap",
      p: "Bridge a slow receivable cycle without missing a pay period or shorting a key vendor.",
    },
    {
      h: "Restock inventory before a busy season",
      p: "Buy ahead at better unit pricing and have shelves full when Dallas demand spikes.",
    },
    {
      h: "Take on a larger contract",
      p: "Fund the upfront materials and labor required to accept a bid you'd otherwise have to pass on.",
    },
    {
      h: "Refinance higher-cost debt",
      p: "Consolidate stacked MCAs or short-term advances into a cleaner, longer-term structure.",
    },
    {
      h: "Open a second location",
      p: "Cover build-out, deposits, and ramp-up payroll until the new site reaches steady revenue.",
    },
    {
      h: "Buy or upgrade equipment",
      p: "Acquire the machinery, vehicles, or tech you need without draining operating cash.",
    },
  ];
  const overrides: Record<string, { h: string; p: string }[]> = {
    "line-of-credit": [
      {
        h: "Float seasonal swings",
        p: "Draw during slow months, repay during peaks. You only pay interest on what you use.",
      },
      {
        h: "Cover surprise repairs",
        p: "Replace a failed HVAC, compressor, or vehicle without an emergency-rate loan.",
      },
      {
        h: "Bid on bigger projects",
        p: "Show working capital reserves on RFPs and proposals without tying up your cash.",
      },
    ],
    "equipment-financing": [
      {
        h: "Buy a work truck or van",
        p: "Get the asset on the road this week; finance over its useful life.",
      },
      {
        h: "Upgrade to better margins",
        p: "Trade an aging unit for newer equipment that lowers fuel, labor, or maintenance cost.",
      },
      {
        h: "Add capacity for a new contract",
        p: "Bring on the second lift, oven, machine, or rig before turning down work.",
      },
    ],
  };
  if (slug.includes("line-of-credit")) return overrides["line-of-credit"];
  if (slug.includes("equipment")) return overrides["equipment-financing"];
  return generic.slice(0, 6);
}

/* ---------- FAQ ---------- */
function Faq({
  pillar,
  product,
  content,
}: {
  pillar: DallasPage;
  product?: LoanProduct;
  content?: PillarContent;
}) {
  const entries =
    content?.faq && content.faq.length
      ? content.faq
      : (pillar.faqQuestions.length
          ? pillar.faqQuestions
          : [
              `how does ${pillar.pillar.toLowerCase()} work in dallas`,
              `what's the typical amount for ${pillar.pillar.toLowerCase()}`,
              `how fast can i get funded`,
              `what documents do i need`,
              `do you charge any upfront fees`,
              `will applying hurt my credit`,
            ]
        )
          .slice(0, 6)
          .map((q) => ({ q, a: faqAnswer(q, pillar, product) }));

  return (
    <section className="border-b border-border bg-background">
      <div className="mx-auto max-w-3xl px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            FAQ
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            {pillar.pillar} questions, answered
          </h2>
        </Reveal>
        <Reveal delay={80}>
          <FaqList
            faqs={entries.map((f) => ({
              q: /\?$/.test(f.q) ? cap(f.q) : `${cap(f.q)}?`,
              a: f.a,
            }))}
          />
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Related ---------- */
function Related({
  related,
  isIndustry,
}: {
  related: DallasPage[];
  isIndustry: boolean;
}) {
  if (!related.length) return null;
  return (
    <section className="border-b border-border bg-secondary/40">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Related financing
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            {isIndustry
              ? "Other industry-specific programs"
              : "Compare other loan programs"}
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Not sure this is the right fit? These are the most common
            alternatives Dallas owners look at alongside it.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((r, i) => {
            const rp = r.slug ? getLoanProduct(r.slug) : undefined;
            return (
              <Reveal key={r.slug!} delay={i * 50}>
                <Link
                  to="/dallas/$slug"
                  params={{ slug: r.slug! }}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-background p-5 transition hover:-translate-y-0.5 hover:border-accent hover:shadow-lg"
                >
                  <div className="flex items-center gap-2 text-accent">
                    <Icon name={iconFor(r.slug!)} color="co" className="h-8 w-8" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">
                      {isIndustry ? "Industry" : "Program"}
                    </span>
                  </div>
                  <h3 className="mt-3 font-serif text-lg font-semibold">
                    {r.pillar}
                  </h3>
                  {rp?.tagline ? (
                    <p className="mt-2 flex-1 text-sm text-muted-foreground">
                      {rp.tagline}
                    </p>
                  ) : null}
                  <p className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-accent">
                    View program{" "}
                    <Icon name="chevronright" color="bl" className="h-3 w-3 transition group-hover:translate-x-0.5"  />
                  </p>
                </Link>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-10">
          <Link
            to="/dallas"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
          >
            ← See all loan programs
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- Final CTA ---------- */
function FinalCta({ pillar }: { pillar: DallasPage }) {
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 20% 100%, oklch(0.42 0.16 250) 0%, transparent 55%), radial-gradient(ellipse at 90% 0%, oklch(0.3 0.1 260) 0%, transparent 50%)",
        }}
      />
      <div className="relative mx-auto max-w-4xl px-6 py-20 text-center lg:px-10">
        <Reveal>
          <p className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-foreground/85">
            <Icon name="users" color="co" className="h-4 w-4"  /> Talk to a Dallas advisor
          </p>
          <h2 className="mt-5 font-serif text-3xl font-semibold leading-tight md:text-5xl">
            Ready to talk {pillar.pillar.toLowerCase()}?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-primary-foreground/80">
            One 15-minute call tells you whether we can place your file,
            what terms to expect, and what your application needs to look
            like first.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
          </div>
          <p className="mt-6 text-xs text-primary-foreground/60">
            {AGENCY.hours} · {AGENCY.address}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
